import React, { useEffect, useRef } from 'react';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import type MapView from '@arcgis/core/views/MapView';
import { useGlobalization } from '@Translations/useGlobalization';

const LOADING_KEY = 'esri-service-loading';
const ERROR_KEY = 'esri-service-error';
const HIDDEN_ASSETS_KEY = 'esri-hidden-assets';
const THEME_LOADING_KEY = 'esri-theme-loading';

export const useServiceBanners = (view: MapView | null) => {
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const { t } = useGlobalization();

	const loadingVisibleRef = useRef(false);
	const errorVisibleRef = useRef(false);
	const hiddenAssetsDismissedRef = useRef(false);
	const hiddenAssetsVisibleRef = useRef(false);
	const allLoadedRef = useRef(false);
	const minZoomForWarningRef = useRef<number | null>(null);
	const zoomWatcherRef = useRef<{ remove: () => void } | null>(null);

	useEffect(() => {
		return () => {
			zoomWatcherRef.current?.remove();
			zoomWatcherRef.current = null;
		};
	}, [view]);

	const showLoading = () => {
		if (loadingVisibleRef.current) return;
		loadingVisibleRef.current = true;
		enqueueSnackbar(t('Loading map...'), {
			key: LOADING_KEY,
			variant: 'info',
			persist: true,
		});
	};

	const hideLoading = () => {
		if (!loadingVisibleRef.current) return;
		loadingVisibleRef.current = false;
		closeSnackbar(LOADING_KEY);
	};

	const themeLoadingVisibleRef = useRef(false);

	const showThemeLoading = () => {
		if (themeLoadingVisibleRef.current) return;
		themeLoadingVisibleRef.current = true;
		enqueueSnackbar(t('Loading theme...'), {
			key: THEME_LOADING_KEY,
			variant: 'info',
			persist: true,
		});
	};

	const hideThemeLoading = () => {
		if (!themeLoadingVisibleRef.current) return;
		themeLoadingVisibleRef.current = false;
		closeSnackbar(THEME_LOADING_KEY);
	};

	const showError = () => {
		hideLoading();
		errorVisibleRef.current = true;
		enqueueSnackbar(t('Could not load all the layers'), {
			key: ERROR_KEY,
			variant: 'error',
			persist: true,
		});
	};

	const showHiddenAssets = () => {
		if (hiddenAssetsVisibleRef.current) return;
		hiddenAssetsVisibleRef.current = true;
		enqueueSnackbar(t('Not all assets are visible at this zoom level.'), {
			key: HIDDEN_ASSETS_KEY,
			variant: 'info',
			persist: true,
			action: (snackbarId) =>
				React.createElement(
					IconButton,
					{
						size: 'small',
						color: 'inherit',
						onClick: () => {
							hiddenAssetsDismissedRef.current = true;
							hiddenAssetsVisibleRef.current = false;
							closeSnackbar(snackbarId);
						},
					},
					React.createElement(CloseIcon, { fontSize: 'small' }),
				),
		});
	};

	const evaluateHiddenAssets = (zoom: number) => {
		const minZoom = minZoomForWarningRef.current;
		if (
			allLoadedRef.current &&
			!errorVisibleRef.current &&
			!hiddenAssetsDismissedRef.current &&
			minZoom !== null &&
			zoom < minZoom
		) {
			showHiddenAssets();
		}
	};

	const resetDismissedState = () => {
		hiddenAssetsDismissedRef.current = false;
		allLoadedRef.current = false;
		hiddenAssetsVisibleRef.current = false;
	};

	const onServiceLoading = () => {
		resetDismissedState();
		showLoading();
	};

	const onServicesAllLoaded = () => {
		allLoadedRef.current = true;
		hideLoading();

		if (view && !zoomWatcherRef.current) {
			import('@arcgis/core/core/reactiveUtils').then(
				({ watch }) => {
					if (!view || zoomWatcherRef.current) return;
					zoomWatcherRef.current = watch(
						() => view.zoom,
						(zoom) => evaluateHiddenAssets(zoom),
						{ initial: true },
					);
				},
			);
		}
	};

	const onServiceError = (criticalError: boolean) => {
		if (criticalError) {
			showError();
		}
	};

	const onMinZoom = (minZoom: number) => {
		minZoomForWarningRef.current = Math.max(
			minZoomForWarningRef.current ?? 0,
			minZoom,
		);
	};

	const onResetMinZoom = () => {
		minZoomForWarningRef.current = null;
	};

	return {
		onServiceLoading,
		onServicesAllLoaded,
		onServiceError,
		onMinZoom,
		onResetMinZoom,
		showThemeLoading,
		hideThemeLoading,
	};
};
